// src/components/atomic-crm/contacts/ContactCompanyImportButton.tsx
import { useEffect, useState } from "react";
import type { MouseEvent } from "react";
import { Upload, Loader2, Building2, User } from "lucide-react";
import { Form, useRefresh, useTranslate, useStore } from "ra-core";
import { Link } from "react-router";
import { Alert, AlertDescription } from "@/components/ds/ui/alert";
import { Button } from "@/components/ds/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ds/ui/dialog";
import { FormToolbar } from "@/components/ds/admin/simple-form";
import { FileInput } from "@/components/ds/admin/file-input";
import { FileField } from "@/components/ds/admin/file-field";
import { Switch } from "@/components/ds/ui/switch";

import { usePapaParse } from "../misc/usePapaParse";
import type { ContactCompanyImportSchema } from "./useContactCompanyImport";
import { useContactCompanyImport } from "./useContactCompanyImport";

const SAMPLE_CSV = `first_name,last_name,email,phone,title,company_name,company_website,company_sector
Kirtikar,Ojha,kirtikar.ojha@larsentoubro.com,+91-9876543210,CMO,L&T Infotech,lntinfotech.com,Information Technology
Paresh,Shetty,paresh.shetty@redingtongroup.com,,VP Sales,Redington,redingtongroup.com,Information Technology`;

const SAMPLE_URL = `data:text/csv;name=contacts_with_companies.csv;charset=utf-8,${encodeURIComponent(SAMPLE_CSV)}`;

export const ContactCompanyImportButton = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const translate = useTranslate();

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setModalOpen(true)}
        className="flex items-center gap-2 cursor-pointer"
      >
        <Upload className="h-4 w-4" />
        {translate("crm.contact.action.import", { _: "Import" })}
      </Button>
      <ContactCompanyImportDialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
};

function ContactCompanyImportDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose(): void;
}) {
  const refresh = useRefresh();
  const translate = useTranslate();
  const processBatch = useContactCompanyImport();
  const [autoEnrich, setAutoEnrich] = useStore<boolean>(
    "crm.enrichment.autoOnImport",
    false,
  );

  const { importer, parseCsv, reset } =
    usePapaParse<ContactCompanyImportSchema>({
      batchSize: 10,
      processBatch,
    });

  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    if (importer.state === "complete") {
      refresh();
    }
  }, [importer.state, refresh]);

  const handleClose = () => {
    reset();
    setFile(null);
    onClose();
  };

  const handleReset = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <Form className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>
              {translate("crm.import.contacts_companies.title", {
                _: "Import Contacts & Companies",
              })}
            </DialogTitle>
            <DialogDescription>
              {translate("crm.import.contacts_companies.description", {
                _: "Upload a CSV with contacts. Company columns are optional — each contact will be linked to its company automatically.",
              })}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col space-y-3">
            {importer.state === "running" && (
              <Alert>
                <AlertDescription className="flex flex-row gap-4">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  {translate("crm.contact.import.running", {
                    _: "Importing...",
                  })}
                </AlertDescription>
              </Alert>
            )}

            {importer.state === "running" && (
              <div className="text-sm text-muted-foreground">
                {importer.importCount} / {importer.rowCount} rows
                {importer.errorCount > 0 && (
                  <> · {importer.errorCount} errors</>
                )}
                {importer.remainingTime !== null && (
                  <> · {millisecondsToTime(importer.remainingTime)} left</>
                )}
                {" · "}
                <button
                  onClick={handleReset}
                  className="text-red-500 underline hover:text-red-700"
                >
                  Stop
                </button>
              </div>
            )}

            {importer.state === "error" && (
              <Alert variant="destructive">
                <AlertDescription>
                  Import failed. Check the console for details.
                </AlertDescription>
              </Alert>
            )}

            {importer.state === "complete" && (
              <Alert>
                <AlertDescription>
                  ✅ Imported {importer.importCount} contacts
                  {importer.errorCount > 0 &&
                    ` (${importer.errorCount} errors)`}
                  . Companies were created as needed.
                </AlertDescription>
              </Alert>
            )}

            {importer.state === "idle" && (
              <>
                <Alert>
                  <AlertDescription className="flex flex-col gap-3">
                    <span>
                      Company columns are optional. If you include them,
                      companies will be matched by website domain (or name) and
                      created if missing.
                    </span>
                    <Button asChild variant="outline" size="sm">
                      <Link
                        to={SAMPLE_URL}
                        download="contacts_with_companies.csv"
                      >
                        Download sample CSV
                      </Link>
                    </Button>
                  </AlertDescription>
                </Alert>

                <FileInput
                  source="csv"
                  label="CSV file"
                  accept={{ "text/csv": [".csv"] }}
                  onChange={setFile}
                >
                  <FileField source="src" title="title" target="_blank" />
                </FileInput>

                <div className="flex items-start gap-3 p-3 rounded-md border bg-muted/30">
                  <Switch
                    checked={autoEnrich}
                    onCheckedChange={setAutoEnrich}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">
                        Enrich with AI
                      </span>
                      {!autoEnrich && (
                        <span className="text-xs text-muted-foreground">
                          (off by default)
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Generate AI profiles for imported records. Slower, uses
                      Exa + LLM API credits.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </Form>

        <div className="flex justify-end pt-4">
          <FormToolbar>
            {importer.state === "idle" ? (
              <Button onClick={() => file && parseCsv(file)} disabled={!file}>
                Import
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={importer.state === "running"}
              >
                {importer.state === "complete" ? "Done" : "Cancel"}
              </Button>
            )}
          </FormToolbar>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function millisecondsToTime(ms: number) {
  const seconds = Math.floor((ms / 1000) % 60);
  const minutes = Math.floor((ms / (60 * 1000)) % 60);
  return `${minutes}m ${seconds}s`;
}
